import { compose } from "redux";
import times from "lodash/times";
import PropTypes from "prop-types";
import isString from "lodash/isString";
import React, { PureComponent } from "react";
import { Grid, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import classNames from "classnames";

import { invokeIfFunction } from "utils/app.util";
import { SORT_DIRECTION } from "features/planner/planner.constants";
import SortableListRow from "app/components/SortableList/SortableListRow";
import SortableListHeader from "app/components/SortableList/SortableListHeader";
import SortableListRowIcon from "app/components/SortableList/SortableListRowIcon";
import SortableListRowColumn from "app/components/SortableList/SortableListRowColumn";

class SortableList extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      sortDirection: props.defaultSortDirection,
      sortedColumnNo: props.defaultSortColumn,
    };
  }

  renderItem = item => {
    const {
      noborder,
      noheader,
      Icon,
      getKey,
      actions,
      columns,
      onRowClick,
      toggleEditModal,
      renderIcon,
      createSortableListRowProps,
    } = this.props;

    const onClick = onRowClick
      ? () => onRowClick(item, toggleEditModal)
      : undefined;

    const icon = Icon ? (
      <SortableListRowIcon>
        <Icon color="secondary" />
      </SortableListRowIcon>
    ) : (
      invokeIfFunction(renderIcon, item)
    );

    return (
      <SortableListRow
        noborder={noborder}
        noheader={noheader}
        key={getKey(item)}
        onClick={onClick}
        Icon={icon}
        Action={
          actions &&
          actions.map((action, index) => {
            const { getValue, ...rest } = action;

            return (
              <SortableListRowIcon key={index} transparent {...rest}>
                {invokeIfFunction(getValue, item)}
              </SortableListRowIcon>
            );
          })
        }
        {...invokeIfFunction(createSortableListRowProps, item)}
      >
        {columns.map((column, index) => {
          const { getSortProperty, getValue, xs, render, ...rest } = column;

          if (render) {
            return invokeIfFunction(render, item, index);
          }

          return (
            <SortableListRowColumn key={index} item xs={xs} {...rest}>
              {!getValue && invokeIfFunction(getSortProperty, item)}
              {invokeIfFunction(getValue, item)}
            </SortableListRowColumn>
          );
        })}
      </SortableListRow>
    );
  };

  setSortColumn = columnNo => {
    const { sortDirection, sortedColumnNo } = this.state;

    this.setState({
      sortedColumnNo: columnNo,
      sortDirection:
        sortedColumnNo === columnNo
          ? sortDirection === SORT_DIRECTION.ASC
            ? SORT_DIRECTION.DESC
            : SORT_DIRECTION.ASC
          : SORT_DIRECTION.ASC,
    });
  };

  order = items => {
    const { columns } = this.props;
    const { sortDirection, sortedColumnNo } = this.state;

    if (!columns[sortedColumnNo]) {
      return items;
    }

    const { getSortProperty } = columns[sortedColumnNo];

    if (getSortProperty) {
      const sortedItems = items.sortBy(item => {
        const property = getSortProperty(item);

        return property ? property.toString().toLowerCase() : undefined;
      });

      return sortDirection === SORT_DIRECTION.ASC
        ? sortedItems
        : sortedItems.reverse();
    }

    return items;
  };

  filterItem = item => {
    const { columns, filter } = this.props;

    return filter
      ? columns
          .map(({ getSortProperty }) =>
            getSortProperty ? getSortProperty(item) : undefined,
          )
          .filter(Boolean)
          .some(value =>
            value
              .toString()
              .toLowerCase()
              .includes(filter.trim().toLowerCase()),
          )
      : true;
  };

  render() {
    const {
      classes,
      items,
      dataState,
      noborder,
      noheader,
      columns,
      actions,
      minItems,
      displayStatusIcon,
      noResultsMessage,
      title,
      stickyHeader,
    } = this.props;

    const { sortedColumnNo } = this.state;

    const sortedItems = this.order(items.filter(this.filterItem));

    return (
      <>
        <Grid
          className={classNames({
            [classes.headerRootSticky]: stickyHeader,
          })}
          container
        >
          {!!title && (
            <SortableListRow noborder classes={{ root: classes.rootRow }}>
              <Grid item>
                <Typography variant="h6">{title}</Typography>
              </Grid>
            </SortableListRow>
          )}
          {!noheader && (
            <SortableListHeader
              noborder={noborder}
              actions={actions}
              columns={columns}
              sortedColumnNo={sortedColumnNo}
              setSortColumn={this.setSortColumn}
              displayStatusIcon={displayStatusIcon}
              sortDirection={this.state.sortDirection}
            />
          )}
        </Grid>
        {!items.size && noResultsMessage && !dataState.isLoading() && (
          <SortableListRow striped={false} noborder>
            <Grid container justifyContent="center">
              {isString(noResultsMessage) ? (
                <Typography variant="caption">{noResultsMessage}</Typography>
              ) : (
                { noResultsMessage }
              )}
            </Grid>
          </SortableListRow>
        )}
        {sortedItems.map(this.renderItem)}
        {/* Reserve space for 'minItems' items */}
        {times(minItems - sortedItems.size, index => (
          <SortableListRow
            key={index}
            style={{
              visibility: "hidden",
            }}
          ></SortableListRow>
        ))}
      </>
    );
  }
}

SortableList.defaultProps = {
  minItems: 0,
  defaultSortColumn: 0,
  defaultSortDirection: SORT_DIRECTION.ASC,
  stickyHeader: true,
};

SortableList.propTypes = {
  noborder: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      getSortProperty: PropTypes.func,
    }),
  ),
};

const styles = theme => ({
  headerRootSticky: {
    top: 0,
    zIndex: 2,
    position: "sticky",
  },
  rootRow: {
    height: "auto",
    padding: theme.spacing(0.5),
    background: theme.palette.background.paper,
  },
});

export default compose(withStyles(styles))(SortableList);
